'use client'

import { useState, useCallback, useEffect } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { Box, Container } from '@mui/material'
import { useForm, useWatch } from 'react-hook-form'
import { enqueueSnackbar } from 'notistack'
import JalaliX from '@jalalix/typescript'

// Types
import type { FieldValues } from 'react-hook-form'
import type { APISMSLoginRegisterRequest, APISMSLoginRegisterResult } from '@/types/api/(user)/sms/login-register'
import type { APISMSVerificationRequest, APISMSVerificationResult } from '@/types/api/(user)/sms/verification'
import type { LoginProps } from '@/types/components/pages/main/login'

// Configurations
import Config from '@/config'
import APIConfig from '@/config/api'

// Helpers
import Cookies from '@/helpers/cookies'
import { useUserStore } from '@/helpers/store/user'

// Utils
import URLParams from '@/utils/api/url-params'

// Components
const MobileFormOrganism = dynamic(() => import('@/components/organisms/forms/main/login/mobile'))
const VerificationCodeOrganism = dynamic(() => import('@/components/organisms/forms/main/login/verification-code'))

const LoginPage = (props: LoginProps) => {
	// Props
	const { lng, locale } = props

	// Variables
	const router = useRouter()
	const { setIsLogin, setLastCheckLoginDate, setUUID, setUser_Name, setUsername, setEmail, setPicture, setGroup } = useUserStore()
	const [step, setStep] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(false)
	const [timer, setTimer] = useState<number>(0)

	// Form
	const mobileForm = useForm<FieldValues>({
		mode: 'onChange',
		defaultValues: { username: '' }
	})

	const verificationCodeForm = useForm<FieldValues>({
		mode: 'onChange',
		defaultValues: { code: Array.from(Array(APIConfig.verificationNumber).keys()).map(() => ({ value: '' })) }
	})

	const mobileValue: string = useWatch({ control: mobileForm.control, name: 'username' })
	const codeValues: { value: string }[] = useWatch({ control: verificationCodeForm.control, name: 'code' })
	const codeValue: string = codeValues.map((item: { value: string }) => item.value.trim()).join('')

	// Callbacks
	const loginRegisterRequest = useCallback(
		async (props: APISMSLoginRegisterRequest) => {
			// Loading
			setLoading(true)

			// Request
			const res = await fetch(`${APIConfig.appUrl}/user/sms/login-register?${URLParams(props)}`, {
				method: 'POST',
				headers: { 'Accept-Language': lng }
			})

			// Result
			const data: APISMSLoginRegisterResult = await res.json()

			// Loading
			setLoading(false)

			// Check Result
			if (data.status) {
				setStep(true)
				setTimer(APIConfig.verificationTimer)
			}

			if (data.data?.time) {
				setStep(true)
				setTimer(Math.ceil(data.data?.time))
			}

			// Check Message
			if (data.message) enqueueSnackbar(data.message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 })
			if (data.messages) data.messages.map(message => enqueueSnackbar(message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 }))
		},
		[lng]
	)

	const verificationRequest = useCallback(
		async (props: APISMSVerificationRequest) => {
			// Loading
			setLoading(true)

			// Request
			const res = await fetch(`${APIConfig.appUrl}/user/sms/verification?${URLParams(props)}`, {
				method: 'PUT',
				headers: { 'Accept-Language': lng }
			})

			// Result
			const data: APISMSVerificationResult = await res.json()

			// Check Result
			if (data.status) {
				setIsLogin(true)

				if (data.data?.item) {
					const user = data.data.item
					setLastCheckLoginDate(new JalaliX().toDateString())

					if (user) {
						if (user.uuid) setUUID(user.uuid)

						setUser_Name(user.user_name)
						setPicture(user.picture)

						if (user.username) setUsername(user.username)
						if (user.email) setEmail(user.email)
						if (user.group) setGroup(user.group)
					}
				}

				// Set Cookie
				if (data.data?.tokenValue) {
					const isSecureContext = typeof window !== 'undefined' && ['https:'].includes(window.location.protocol)

					Cookies.set(`${Config.shortName}-token`, data.data.tokenValue, {
						path: '/',
						secure: isSecureContext,
						sameSite: 'lax',
						expires: new Date(data.data.token.expires_date ?? Date.now() + 86400000 * 7)
					})
				}

				router.push(`/${lng}`)
			} else
				// Loading
				setLoading(false)

			// Check Message
			if (data.message) enqueueSnackbar(data.message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 })
			if (data.messages) data.messages.map(message => enqueueSnackbar(message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 }))
		},
		[lng, router, setIsLogin, setLastCheckLoginDate, setUUID, setUser_Name, setUsername, setEmail, setPicture, setGroup]
	)

	const handleInterval = useCallback(() => {
		if (timer > 0) setTimer(timer - 1)
	}, [timer, setTimer])

	useEffect(() => {
		const interval = setInterval(handleInterval, 1000)
		return () => clearInterval(interval)
	}, [timer, handleInterval])

	return (
		<Container maxWidth="xs" sx={{ height: '100dvh' }}>
			<Box
				sx={{
					display: step ? 'none' : 'inline-flex',
					width: 1,
					height: 1
				}}
			>
				<MobileFormOrganism
					lng={lng}
					locale={locale}
					control={mobileForm.control}
					formState={mobileForm.formState}
					loading={loading}
					onConfirm={() => loginRegisterRequest({ username: mobileValue })}
				/>
			</Box>

			<Box
				sx={{
					display: step ? 'inline-flex' : 'none',
					width: 1,
					height: 1
				}}
			>
				<VerificationCodeOrganism
					lng={lng}
					locale={locale}
					control={verificationCodeForm.control}
					formState={verificationCodeForm.formState}
					loading={loading}
					values={{ username: mobileValue, timer }}
					setValue={verificationCodeForm.setValue}
					setFocus={verificationCodeForm.setFocus}
					getValues={verificationCodeForm.getValues}
					reset={verificationCodeForm.reset}
					resetField={verificationCodeForm.resetField}
					onConfirm={() => verificationRequest({ username: mobileValue, code: codeValue })}
					onReset={() => {
						verificationCodeForm.reset({ code: Array.from(Array(APIConfig.verificationNumber).keys()).map(() => ({ value: '' })) })

						loginRegisterRequest({ username: mobileValue })
						setTimer(APIConfig.verificationTimer)
					}}
					onCancel={() => setStep(false)}
				/>
			</Box>
		</Container>
	)
}

export default LoginPage
